import { useDrop } from '@modules/wholesale/dropSettings/graphql/hooks'
import Typography from '@material-ui/core/Typography'
import { stores, strings } from '@stores'
import { useObserver } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import filter from 'lodash/filter'

import * as css from './BulkOperationPopupContent.css'
import { BulkOperationTabContent } from './BulkOperationTabContent'
import { hasUserRoles, PERMISSION_MAP } from '@services/userRoleService'
import { useBuyingSessionSidebarContext } from '@modules/wholesale/wsNavigations/contexts/use-buyingSession-sidebar-context'
import TabsStrip, { ITab } from '@components/std-components/TabsStrip'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { useClients } from '@modules/wholesale/clientSettings/graphql/hooks'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { useTranslation } from 'react-i18next'
import { CriteriaOperationType } from '@models'
import formatNumberByLocale from '@utils/format-number-by-locale'

interface IBulkOperationPopupContentProps {
  selectedChannels?: [string]
  buyingSessionGroupStatuses?: Record<string, string>
  bsgExcludedClientIds?: [string]
}

export const BulkOperationPopupContent: React.FunctionComponent<
  IBulkOperationPopupContentProps
> = ({ selectedChannels, buyingSessionGroupStatuses, bsgExcludedClientIds }) => {
  return useObserver(function useHook() {
    const { t } = useTranslation()
    let {
      wsBuyingSessionProduct: {
        setBulkOpSelectedTab,
        bulkOpSelectedTab,
        handleBulkOpTabChange,
        productSelectionStore: { countOfBulkActionSelectedProducts }
      },
      nav: { buyingSessionGroupId },
      freeTagListStore: {
        tagsLoading,
        wholesaleFreeTags,
        retailFreeTags,
        getFreeTagsByBSGId
      }
    } = stores
    const {
      buyingSessionSidebarStore: {
        getAllClustersData,
        setSelectedChannelValues,
        selectedChannelsValues,
        wholesaleClusters,
        retailClusters,
        isClustersLoading,
        tabHeaders,
        getSalesPeriods,
        salesPeriods
      }
    } = useBuyingSessionSidebarContext()
    if (!bulkOpSelectedTab) {
      setBulkOpSelectedTab(strings.tagTypes.channel)
    }
    React.useEffect(() => {
      selectedChannels &&
        setSelectedChannelValues(selectedChannels, buyingSessionGroupStatuses)
      selectedChannels && getAllClustersData(selectedChannels)
      selectedChannels && getFreeTagsByBSGId(buyingSessionGroupId)
      selectedChannels?.includes(VIPChannel.Retail) &&
        getSalesPeriods(buyingSessionGroupId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChannels, buyingSessionGroupId])

    // Drops data
    const { loading: dropLoading, data: dropData } = useDrop(buyingSessionGroupId)

    // Client and Zone Exclusion data
    const { data: clientSearchData } = useClients({
      GIV_VIPClient_Active_bool: { operation: CriteriaOperationType.EQ, value: true }
    })
    const clientExclusionData = clientSearchData?.findVIPClient?.Items.filter(
      client =>
        client.ClientType === VIPClientType.External &&
        !bsgExcludedClientIds?.includes(client.Id)
    )
    const zoneExclusionData = clientSearchData?.findVIPClient?.Items.filter(
      client =>
        client.ClientType === VIPClientType.Internal &&
        !bsgExcludedClientIds?.includes(client.Id)
    )
    const authorizedUserRolesByChannel = useMemo(
      () => filter(PERMISSION_MAP, cluster => hasUserRoles(cluster.allowedRoles)),
      []
    )

    return (
      <div className={css.bulkPopupContent}>
        <div className={css.popupHeader}>
          <div className={css.tabsWrapper}>
            <TabsStrip
              showTabNavIndicators={true}
              tabHeaders={
                tabHeaders(
                  selectedChannels,
                  authorizedUserRolesByChannel,
                  buyingSessionGroupStatuses
                ) as ITab[]
              }
              onChange={({ tab }) => handleBulkOpTabChange(tab)}
            />
          </div>
          <Typography className={css.productsCountText}>
            {`${formatNumberByLocale(countOfBulkActionSelectedProducts)} ${
              countOfBulkActionSelectedProducts > 1
                ? t('bb.common.label.productsSelected', 'Products selected')
                : t('bb.common.label.productSelected', 'Product selected')
            }`}
          </Typography>
        </div>
        <BulkOperationTabContent
          isLoading={isClustersLoading || dropLoading || tagsLoading}
          allChannels={selectedChannelsValues}
          wholesaleClusters={wholesaleClusters}
          allDrops={dropData?.findVIPSalesPeriod?.Items ?? []}
          wholesaleFreeTags={wholesaleFreeTags}
          clientExclusions={clientExclusionData}
          retailClusters={retailClusters}
          retailFreeTags={retailFreeTags}
          salesPeriods={salesPeriods}
          zoneExclusions={zoneExclusionData}
          wholesaleClusterExclusions={wholesaleClusters ?? []}
          retailClusterExclusions={retailClusters ?? []}
        />
      </div>
    )
  })
}
